import React from "react";
import {
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  Typography,
} from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { colors } from "../../utils/colors";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [6, 12, 24, 48],
}) => {
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        backgroundColor: colors.cardBackground,
        borderTop: `1px solid ${colors.background}`,
        fontFamily: "SigmaSans",
      }}
    >
      {/* Results per page dropdown */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: colors.primaryText,
            fontFamily: "SigmaSans",
            fontSize: "14px",
          }}
        >
          Result per page:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            variant="outlined"
            sx={{
              fontFamily: "SigmaSans",
              "& .MuiSelect-select": {
                fontFamily: "SigmaSans",
                fontSize: "14px",
              },
            }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem
                key={option}
                value={option}
                sx={{
                  fontFamily: "SigmaSans",
                  fontSize: "14px",
                }}
              >
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Previous button */}
          <Button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            startIcon={<KeyboardArrowLeft />}
            sx={{
              backgroundColor: colors.cardBackground,
              color: colors.primaryText,
              border: `1px solid #E0E0E0`,
              borderRadius: "4px",
              textTransform: "none",
              fontFamily: "SigmaSans",
              fontSize: "14px",
              padding: "8px 16px",
              minWidth: "auto",
              "&:hover": {
                backgroundColor: colors.background,
                borderColor: colors.primaryText,
              },
              "&:disabled": {
                color: colors.secondaryText,
                backgroundColor: colors.cardBackground,
              },
            }}
          >
            Previous
          </Button>

          {/* Page numbers */}
          {visiblePages.map((page, index) => (
            <React.Fragment key={index}>
              {page === "..." ? (
                <Typography
                  sx={{
                    color: colors.primaryText,
                    fontFamily: "SigmaSans",
                    fontSize: "14px",
                    padding: "8px 12px",
                  }}
                >
                  --
                </Typography>
              ) : (
                <Button
                  onClick={() => onPageChange(Number(page))}
                  sx={{
                    backgroundColor:
                      currentPage === page
                        ? colors.volvoBlue
                        : colors.cardBackground,
                    color: currentPage === page ? "white" : colors.primaryText,
                    border: `1px solid ${
                      currentPage === page ? colors.volvoBlue : "#E0E0E0"
                    }`,
                    borderRadius: "4px",
                    textTransform: "none",
                    fontFamily: "SigmaSans",
                    fontSize: "14px",
                    padding: "8px 12px",
                    minWidth: "40px",
                    height: "40px",
                    "&:hover": {
                      backgroundColor:
                        currentPage === page
                          ? colors.complementaryBlue
                          : colors.background,
                      borderColor:
                        currentPage === page
                          ? colors.complementaryBlue
                          : colors.primaryText,
                    },
                  }}
                >
                  {page}
                </Button>
              )}
            </React.Fragment>
          ))}

          <Button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            endIcon={<KeyboardArrowRight />}
            sx={{
              backgroundColor: colors.cardBackground,
              color: colors.primaryText,
              border: `1px solid #E0E0E0`,
              borderRadius: "4px",
              textTransform: "none",
              fontFamily: "SigmaSans",
              fontSize: "14px",
              padding: "8px 16px",
              minWidth: "auto",
              "&:hover": {
                backgroundColor: colors.background,
                borderColor: colors.primaryText,
              },
              "&:disabled": {
                color: colors.secondaryText,
                backgroundColor: colors.cardBackground,
              },
            }}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Pagination;
